// @ts-strict-ignore
import React, { useMemo } from 'react';
import {
  Box,
  Card,
} from '@usealloy/component-library';

import { useEntityPiiPrevious } from '~/entities/hooks';
import type { Evaluation } from '~/entities/types';

import { useEventTypeEvaluationsContext } from '../../context';
import { useDuplicatePii } from '../../hooks';
import { YourCustomerItem } from './YourCustomerItem';
import { DISPLAY_NAME_MAPPING, DUPLICATE_PII_MAPPING, PII_MAPPING } from './constants';

interface YourCustomerProps {
  header?: string;
  useExistingPiiOnly?: boolean;
}

const YourCustomer: React.FC<YourCustomerProps> = ({
  header = 'Your Customer',
  useExistingPiiOnly = false,
}) => {
  const { evaluation, categories } = useEventTypeEvaluationsContext();
  const { data: entityPiiPrevious } = useEntityPiiPrevious({
    entityToken: evaluation.entity_token,
    before: evaluation.event_request_data?.timestamp,
  });
  const { data: duplicatePii } = useDuplicatePii({
    evaluationToken: evaluation.evaluation_token,
  });
  const records = useMemo(() => {
    const { supplied } = evaluation.final_response ?? {};

    const piiRecords = Object.entries(PII_MAPPING)
      .reduce((acc, [pii, inputs]) => {
        const isSupplied = inputs.reduce((prev, input) => prev || Boolean(supplied[input]), false);

        if (!isSupplied) {
          return acc;
        }

        acc.push(pii);

        return acc;
      }, []);

    return categories.filter(category => (
      piiRecords.includes(category.name)
      && !(
        ['name', 'address'].includes(category.name.split('_')[0])
        && category.name.split('_').length > 1
      )
    ));
  }, []);

  return (
    <Card
      header={header}
      collapsible
    >
      <Box
        direction="column"
        gap={5}
      >
        {records.map(({ name }) => {
          const {
            evaluationsCount = 0,
            entitiesCount = 0,
            sandbox = false,
          } = duplicatePii[DUPLICATE_PII_MAPPING[name]] ?? {};

          return (
            name === 'address' ? (
              evaluation?.final_response?.formatted?.addresses?.map((address, i) => {
                if (address.type === 'primary') {
                  return null;
                }

                const formattedAddress = Object.fromEntries(
                  Object
                    .entries(evaluation?.final_response?.formatted?.addresses?.[i] ?? {})
                    .map(([key, val]) => ([`address_${key}`, val])),
                );
                const matchingAddress = Object.fromEntries(
                  Object
                    .entries(evaluation?.final_response?.matching?.addresses?.[i] ?? {})
                    .map(([key, val]) => ([`address_${key}`, val])),
                );
                const entityPiiPreviousAddress = entityPiiPrevious?.addresses
                  ?.find(({ address_type: addressType }) => addressType === address.type);

                return (
                  <YourCustomerItem
                    key={`${address.type}_${name}`}
                    name={name}
                    displayName={`${address.type}_${name}`}
                    formatted={formattedAddress as Evaluation['final_response']['formatted']}
                    matching={matchingAddress as Evaluation['final_response']['matching']}
                    entityPiiPrevious={entityPiiPreviousAddress ?? {}}
                    duplicateEvaluationsCount={evaluationsCount - 1}
                    duplicateEntitiesCount={entitiesCount - 1}
                    isSandbox={sandbox}
                    useExistingPiiOnly={useExistingPiiOnly}
                  />
                );
              })
            ) : (
              <YourCustomerItem
                key={name}
                name={name}
                displayName={DISPLAY_NAME_MAPPING[name] ?? null}
                formatted={evaluation?.final_response?.formatted}
                matching={evaluation?.final_response?.matching}
                entityPiiPrevious={entityPiiPrevious ?? {}}
                duplicateEvaluationsCount={evaluationsCount - 1}
                duplicateEntitiesCount={entitiesCount - 1}
                isSandbox={sandbox}
                useExistingPiiOnly={useExistingPiiOnly}
              />
            )
          );
        })}
      </Box>
    </Card>
  );
};

export default YourCustomer;
